import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Section } from "design-system/molecules/Section"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import config from "lib/config"
import { getErrorTranslations } from "lib/i18n"
import { OrganizationSelector } from "modules/backoffice/components/OrganizationSelector"
import { useCreateBackofficeCompanyMutation } from "modules/backoffice/hooks/companies/useCreateBackofficeCompanyMutation"
import { useImpersonateOrganizationMutation } from "modules/backoffice/hooks/organizations/useImpersonateOrganizationMutation"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CreateBackofficeCompanyBodySchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"

function generateRandomPassword(length: number = 16): string {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%^&*"
  const array = new Uint32Array(length)
  self.crypto.getRandomValues(array)

  let password = ""
  for (let i = 0; i < length; i++) {
    const randomIndex = array[i]! % alphabet.length
    password += alphabet[randomIndex]!
  }

  return password
}

export const BackofficeCompanyCreator = () => {
  const { t } = useTranslation()
  const form = useForm<z.infer<typeof CreateBackofficeCompanyBodySchema>>({
    resolver: zodResolver(CreateBackofficeCompanyBodySchema),
    defaultValues: {
      company: { locale: "es_ES" },
      employee: {
        password: config.isProduction === true ? generateRandomPassword() : "pepepermut44",
      },
    },
  })
  const { mutateAsync } = useCreateBackofficeCompanyMutation()
  const { mutateAsync: impersonateOrganizationMutation } = useImpersonateOrganizationMutation()

  // Watch organizationId to disable employee fields when existing org is selected
  const selectedOrganizationId = form.watch("organizationId")
  const isExistingOrganization = !!selectedOrganizationId

  // Clear employee data when switching to existing organization
  useEffect(() => {
    if (isExistingOrganization) {
      form.setValue("employee", undefined)
    } else {
      // Reset to default when switching back to new organization
      form.setValue("employee", {
        password: config.isProduction === true ? generateRandomPassword() : "pepepermut44",
        name: "",
        email: "",
      })
    }
  }, [isExistingOrganization, form])

  const onSubmit = async (data: z.infer<typeof CreateBackofficeCompanyBodySchema>) => {
    // Clean up the data before sending - only include fields that are actually filled
    const payload: z.infer<typeof CreateBackofficeCompanyBodySchema> = {
      company: data.company,
    }

    // Only include organizationId if provided
    if (data.organizationId) {
      payload.organizationId = data.organizationId
    }

    // Only include employee if it exists (not when organizationId is selected)
    if (data.employee) {
      payload.employee = data.employee
    }

    // Only include customer if all fields are filled
    if (data.customer && data.customer.name && data.customer.phone) {
      payload.customer = {
        name: data.customer.name,
        phone: data.customer.phone,
        locale: data.customer.locale || "es_ES",
      }
    }

    const company = await mutateAsync(payload)
    toast.success(t("backoffice.companies.companyCreator.createSuccess"))

    await impersonateOrganizationMutation(company.data.organization.id)
  }

  return (
    <div className="flex flex-col gap-6">
      <Section
        title={t("backoffice.companies.companyCreator.title")}
        description={t("backoffice.companies.companyCreator.description")}
      />
      <Form
        {...form}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
        errorTranslations={getErrorTranslations()}
      >
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-md font-semibold">
              {t("backoffice.companies.companyCreator.section.organization.title")}
            </div>
            <div className="text-base text-muted-foreground max-w-3xl">
              {t("backoffice.companies.companyCreator.section.organization.description")}
            </div>
          </div>
          <FormField
            control={form.control}
            name="organizationId"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <OrganizationSelector
                    value={field.value}
                    onChange={field.onChange}
                    className="w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-md font-semibold">
              {t("backoffice.companies.companyCreator.section.company.title")}
            </div>
            <div className="text-base text-muted-foreground max-w-3xl">
              {t("backoffice.companies.companyCreator.section.company.description")}
            </div>
          </div>
          <div className="grid lg:grid-cols-2 w-full gap-4">
            <div className="grid lg:grid-cols-2 gap-4 col-span-2">
              <FormField
                control={form.control}
                name="company.name"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      {t("backoffice.companies.companyCreator.section.company.fields.name")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        required
                        onChange={(ev) => {
                          form.setValue(
                            "company.slug",
                            `${ev.target.value.replace(/\s+/g, "-").toLowerCase()}_${(+new Date()).toString(32)}`,
                          )
                          field.onChange(ev)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="company.locale"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("backoffice.companies.companyCreator.section.company.fields.locale")}
                    </FormLabel>
                    <FormControl>
                      <Select
                        value={field.value ?? ""}
                        onValueChange={(value) => {
                          const selectedValue = value === "" ? undefined : value
                          field.onChange(selectedValue)
                        }}
                      >
                        <SelectTrigger className="min-w-44">
                          <SelectValue
                            placeholder={t(
                              "backoffice.companies.companyCreator.section.company.fields.locale",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="es_ES">
                            {t(
                              "backoffice.companies.companyCreator.section.company.fields.localeOptions.es_ES",
                            )}
                          </SelectItem>
                          <SelectItem value="en_US">
                            {t(
                              "backoffice.companies.companyCreator.section.company.fields.localeOptions.en_US",
                            )}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-md font-semibold">
              {t("backoffice.companies.companyCreator.section.employee.title")}
            </div>
            <div className="text-base text-muted-foreground max-w-3xl">
              {t("backoffice.companies.companyCreator.section.employee.description")}
            </div>
          </div>
          <div className="grid lg:grid-cols-3 w-full gap-4">
            <FormField
              control={form.control}
              name="employee.name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("backoffice.companies.companyCreator.section.employee.fields.name")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isExistingOrganization} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="employee.email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("backoffice.companies.companyCreator.section.employee.fields.email")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      {...field}
                      disabled={isExistingOrganization}
                      autoComplete="new-email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="employee.password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("backoffice.companies.companyCreator.section.employee.fields.password")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={isExistingOrganization}
                      autoComplete="new-password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-md font-semibold">
              {t("backoffice.companies.companyCreator.section.customer.title")}
            </div>
            <div className="text-base text-muted-foreground max-w-3xl">
              {t("backoffice.companies.companyCreator.section.customer.description")}
            </div>
          </div>
          <div className="grid lg:grid-cols-3 w-full gap-4">
            <FormField
              control={form.control}
              name="customer.name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("backoffice.companies.companyCreator.section.customer.fields.name")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="customer.phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("backoffice.companies.companyCreator.section.customer.fields.phone")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="customer.locale"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("backoffice.companies.companyCreator.section.customer.fields.locale")}
                  </FormLabel>
                  <FormControl>
                    <Select
                      value={field.value ?? ""}
                      onValueChange={(value) => {
                        const selectedValue = value === "" ? undefined : value
                        field.onChange(selectedValue)
                      }}
                    >
                      <SelectTrigger className="min-w-44">
                        <SelectValue
                          placeholder={t(
                            "backoffice.companies.companyCreator.section.customer.fields.locale",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en_US">
                          {t(
                            "backoffice.companies.companyCreator.section.company.fields.localeOptions.en_US",
                          )}
                        </SelectItem>
                        <SelectItem value="es_ES">
                          {t(
                            "backoffice.companies.companyCreator.section.company.fields.localeOptions.es_ES",
                          )}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button disabled={!form.formState.isValid || form.formState.isSubmitting}>
            {t("backoffice.companies.companyCreator.submit")}
          </Button>
        </div>
      </Form>
    </div>
  )
}
